import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import ProfileEditorDrawerAvatar from 'app/components/ProfileEditor/Drawers/Avatar';
import ProfileEditorDrawerCountries from 'app/components/ProfileEditor/Drawers/Countries';
import ProfileEditorDrawerDreamProject from 'app/components/ProfileEditor/Drawers/DreamProject';
import ProfileEditorDrawerIntroduceYourself from 'app/components/ProfileEditor/Drawers/IntroduceYourself';
import ProfileEditorDrawerLanguages from 'app/components/ProfileEditor/Drawers/Languages';
import ProfileEditorDrawerLinkedin from 'app/components/ProfileEditor/Drawers/Linkedin';
import ProfileEditorDrawerMyRole from 'app/components/ProfileEditor/Drawers/MyRole';
import ProfileEditorDrawerOutsideWork from 'app/components/ProfileEditor/Drawers/OutsideWork';
import ProfileEditorDrawerPassions from 'app/components/ProfileEditor/Drawers/Passions';
import ProfileEditorDrawerClients from 'app/components/ProfileEditor/Drawers/PriorExperience/Clients';
import ProfileEditorDrawerCompanies from 'app/components/ProfileEditor/Drawers/PriorExperience/Companies';
import ProfileEditorDrawerIndustries from 'app/components/ProfileEditor/Drawers/PriorExperience/Industries';
import ProfileEditorDrawerSkills from 'app/components/ProfileEditor/Drawers/Skills';
import ProfileEditorDrawerYearsExperience from 'app/components/ProfileEditor/Drawers/YearsExperience';
import EditPencilIcon from 'app/components/shared/icons/edit-pencil';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import useUserData from 'hooks/useUserData';
import { profileCompletionFieldsSelector } from 'store/reducers/user-reducer';

import {
  wayfindindCompletionFieldToDrawer,
  WAYFINDING_DRAWERS,
  WayfindingActiveProfileMenuDrawersType,
  wayfindingDrawerToCTAIcon,
  wayfindingDrawerToIcon,
  wayfindingDrawerToTitle,
} from './constants';
import {
  AboutKyuOsWayfindingActivateProfileMenuContainer,
  AboutKyuOsWayfindingActivateProfileMenuCard,
  AboutKyuOsWayfindingActivateProfileMenuCardTitle,
  AboutKyuOsWayfindingActivateProfileMenuCardIconsWrapper,
  AboutKyuOsWayfindingActivateProfileMenuTitle,
  AboutKyuOsWayfindingActivateProfileMenuWrapper,
  AboutKyuOsWayfindingActivateProfileMenuCardCTAIconContainer,
} from './styled';

export default function AboutKyuOsWayfindingActivateProfileMenu() {
  const behavioralAction = useBehavioral(BEHAVIORALS.KYU101_PROFILE_EDIT_DRAWER_OPENED);
  const profileCompletionFields: { name: string; completed: boolean }[] = useSelector(
    profileCompletionFieldsSelector
  );

  const [drawer, setDrawer] = useState<{
    type: WayfindingActiveProfileMenuDrawersType;
    show: boolean;
  }>({
    type: null,
    show: false,
  });

  const handleCardClick = (type: WayfindingActiveProfileMenuDrawersType) => {
    behavioralAction({ action_details: wayfindingDrawerToTitle.get(type) });
    setDrawer({
      type,
      show: true,
    });
  };

  const completedFields = useMemo(() => {
    return profileCompletionFields.filter((field) => field.completed);
  }, [profileCompletionFields]);

  const uncompletedFields = useMemo(() => {
    return profileCompletionFields.filter((field) => !field.completed);
  }, [profileCompletionFields]);

  return (
    <AboutKyuOsWayfindingActivateProfileMenuWrapper>
      {uncompletedFields.length > 0 ? (
        <>
          <AboutKyuOsWayfindingActivateProfileMenuTitle>
            You have {uncompletedFields.length} out of 14 sections to complete
          </AboutKyuOsWayfindingActivateProfileMenuTitle>
          <AboutKyuOsWayfindingActivateProfileMenuContainer>
            {uncompletedFields.map((field) => (
              <AboutKyuOsWayfindingActivateProfileMenuCard
                key={field.name}
                onClick={() => handleCardClick(wayfindindCompletionFieldToDrawer.get(field.name))}
              >
                <AboutKyuOsWayfindingActivateProfileMenuCardIconsWrapper>
                  {wayfindingDrawerToIcon.get(wayfindindCompletionFieldToDrawer.get(field.name))}
                  <AboutKyuOsWayfindingActivateProfileMenuCardCTAIconContainer>
                    {wayfindingDrawerToCTAIcon.get(
                      wayfindindCompletionFieldToDrawer.get(field.name)
                    )}
                  </AboutKyuOsWayfindingActivateProfileMenuCardCTAIconContainer>
                </AboutKyuOsWayfindingActivateProfileMenuCardIconsWrapper>
                <AboutKyuOsWayfindingActivateProfileMenuCardTitle>
                  {wayfindingDrawerToTitle.get(wayfindindCompletionFieldToDrawer.get(field.name))}
                </AboutKyuOsWayfindingActivateProfileMenuCardTitle>
              </AboutKyuOsWayfindingActivateProfileMenuCard>
            ))}
          </AboutKyuOsWayfindingActivateProfileMenuContainer>
        </>
      ) : null}
      {completedFields.length > 0 ? (
        <>
          <AboutKyuOsWayfindingActivateProfileMenuTitle>
            You have completed {completedFields.length} out of 14 sections
          </AboutKyuOsWayfindingActivateProfileMenuTitle>
          <AboutKyuOsWayfindingActivateProfileMenuContainer>
            {completedFields.map((field) => (
              <AboutKyuOsWayfindingActivateProfileMenuCard
                key={field.name}
                onClick={() => handleCardClick(wayfindindCompletionFieldToDrawer.get(field.name))}
              >
                <AboutKyuOsWayfindingActivateProfileMenuCardIconsWrapper>
                  {wayfindingDrawerToIcon.get(wayfindindCompletionFieldToDrawer.get(field.name))}
                  <AboutKyuOsWayfindingActivateProfileMenuCardCTAIconContainer>
                    {wayfindingDrawerToCTAIcon.get(
                      wayfindindCompletionFieldToDrawer.get(field.name)
                    )}
                  </AboutKyuOsWayfindingActivateProfileMenuCardCTAIconContainer>
                </AboutKyuOsWayfindingActivateProfileMenuCardIconsWrapper>
                <AboutKyuOsWayfindingActivateProfileMenuCardTitle>
                  {wayfindingDrawerToTitle.get(wayfindindCompletionFieldToDrawer.get(field.name))}
                </AboutKyuOsWayfindingActivateProfileMenuCardTitle>
              </AboutKyuOsWayfindingActivateProfileMenuCard>
            ))}
          </AboutKyuOsWayfindingActivateProfileMenuContainer>
        </>
      ) : null}
      <AboutKyuOsWayfindingActivateProfileMenu.Drawers drawer={drawer} setDrawer={setDrawer} />
    </AboutKyuOsWayfindingActivateProfileMenuWrapper>
  );
}

interface AboutKyuOsWayfindingActivateProfileMenuDrawersProps {
  drawer: {
    type: WayfindingActiveProfileMenuDrawersType;
    show: boolean;
  };
  setDrawer: React.Dispatch<
    React.SetStateAction<{
      type: WayfindingActiveProfileMenuDrawersType;
      show: boolean;
    }>
  >;
}

AboutKyuOsWayfindingActivateProfileMenu.Drawers =
  function AboutKyuOsWayfindingActivateProfileMenuDrawers({
    drawer,
    setDrawer,
  }: AboutKyuOsWayfindingActivateProfileMenuDrawersProps) {
    const userData = useUserData();

    const handleDrawerClose = () => {
      setDrawer({
        type: null,
        show: false,
      });
    };

    const wayfindingDrawerTypeToElement = new Map<
      WayfindingActiveProfileMenuDrawersType,
      JSX.Element
    >([
      [WAYFINDING_DRAWERS.PROFILE_IMAGE, <ProfileEditorDrawerAvatar onClose={handleDrawerClose} />],
      [WAYFINDING_DRAWERS.LANGUAGES, <ProfileEditorDrawerLanguages onClose={handleDrawerClose} />],
      [
        WAYFINDING_DRAWERS.EXPERIENCE,
        <ProfileEditorDrawerYearsExperience onClose={handleDrawerClose} />,
      ],
      [WAYFINDING_DRAWERS.ROLE, <ProfileEditorDrawerMyRole onClose={handleDrawerClose} />],
      [
        WAYFINDING_DRAWERS.PRIOR_COMPANIES,
        <ProfileEditorDrawerCompanies
          onClose={handleDrawerClose}
          companyName={userData.kyu_company?.name}
        />,
      ],
      [
        WAYFINDING_DRAWERS.PRIOR_INDUSTRIES,
        <ProfileEditorDrawerIndustries
          onClose={handleDrawerClose}
          companyName={userData.kyu_company?.name}
        />,
      ],
      [
        WAYFINDING_DRAWERS.PRIOR_CLIENTS,
        <ProfileEditorDrawerClients
          onClose={handleDrawerClose}
          companyName={userData.kyu_company?.name}
        />,
      ],
      [WAYFINDING_DRAWERS.SKILLS, <ProfileEditorDrawerSkills onClose={handleDrawerClose} />],
      [WAYFINDING_DRAWERS.PASSIONS, <ProfileEditorDrawerPassions onClose={handleDrawerClose} />],
      [
        WAYFINDING_DRAWERS.BIO,
        <ProfileEditorDrawerIntroduceYourself onClose={handleDrawerClose} />,
      ],
      [
        WAYFINDING_DRAWERS.OUTSIDE_OF_WORK,
        <ProfileEditorDrawerOutsideWork onClose={handleDrawerClose} />,
      ],
      [WAYFINDING_DRAWERS.LINKEDIN, <ProfileEditorDrawerLinkedin onClose={handleDrawerClose} />],
      [
        WAYFINDING_DRAWERS.DREAM_PROJECT,
        <ProfileEditorDrawerDreamProject onClose={handleDrawerClose} />,
      ],
      [WAYFINDING_DRAWERS.LOCATIONS, <ProfileEditorDrawerCountries onClose={handleDrawerClose} />],
    ]);

    if (!drawer.show) return null;

    return wayfindingDrawerTypeToElement.has(drawer.type)
      ? wayfindingDrawerTypeToElement.get(drawer.type)
      : null;
  };
